import type { ApiErrorBody, AuthResponseDto, ErrorCode } from '@pxc/shared';
import { deviceHeaders, deviceReady } from './device.js';
import { getInitData, getStartParam, isTelegramClient } from './telegram.js';

const BASE_URL = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A network failure is not a server error and must not be reported as one. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the server.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

interface Session {
  token: string;
  expiresAt: number;
  /** serverTime − Date.now() at sign-in, used to render honest countdowns (I5). */
  clockOffsetMs: number;
}

let session: Session | null = null;
let inFlightAuth: Promise<AuthResponseDto> | null = null;

export function getSession(): Session | null {
  return session;
}

/**
 * The server's idea of "now", derived from the offset measured at sign-in.
 * Countdowns interpolate against this — they never award anything, they only
 * decide when to ask the server again.
 */
export function serverNow(): number {
  return Date.now() + (session?.clockOffsetMs ?? 0);
}

export function clearSession(): void {
  session = null;
}

function adopt(response: AuthResponseDto): AuthResponseDto {
  session = {
    token: response.token,
    expiresAt: response.expiresAt,
    clockOffsetMs: response.serverTime - Date.now(),
  };
  return response;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body });
}

/**
 * Signs in. Inside Telegram this uses the verified `initData`; in a browser it
 * falls back to the development endpoint, which the server only registers
 * outside production.
 */
type SuspendedListener = (error: ApiError) => void;
const suspendedListeners = new Set<SuspendedListener>();

/**
 * The account was suspended while the app was open: the next request finds
 * out, and the shell gives way to the ban screen (App.tsx). Only requests made
 * from inside the app report it — the sign-in at boot hands its refusal to the
 * shell itself.
 */
export function onSuspended(listener: SuspendedListener): () => void {
  suspendedListeners.add(listener);
  return () => {
    suspendedListeners.delete(listener);
  };
}

function reportSuspended(error: unknown): void {
  // Suspended, kept out of a network another account holds (IP_IN_USE), or
  // not in the official channel: the shell shows the screen for each.
  const blocking = ['USER_SUSPENDED', 'IP_IN_USE', 'IP_BANNED', 'CHANNEL_REQUIRED'];
  if (!(error instanceof ApiError) || !blocking.includes(error.code)) return;
  for (const listener of suspendedListeners) listener(error);
}

export async function authenticate(): Promise<AuthResponseDto> {
  if (inFlightAuth) return inFlightAuth;

  const startParam = getStartParam();
  const initData = getInitData();

  inFlightAuth = (async () => {
    // The sign-in carries everything the app knows about the phone, so it
    // waits for it — the five stores read and the hardware print taken.
    await deviceReady();
    if (initData) {
      return adopt(await post<AuthResponseDto>('/auth/telegram', { initData, startParam }));
    }
    if (import.meta.env.PROD && isTelegramClient()) {
      throw new ApiError('INVALID_INIT_DATA', 'Open this app from Telegram to sign in.', 401);
    }
    // Development: a stable per-browser identity so reloading keeps your mine.
    return adopt(
      await post<AuthResponseDto>('/auth/dev', {
        telegramId: devTelegramId(),
        username: 'dev_miner',
        startParam,
      }),
    );
  })();

  try {
    return await inFlightAuth;
  } finally {
    inFlightAuth = null;
  }
}

const DEV_ID_KEY = 'pxc.dev.telegramId';

function devTelegramId(): number {
  try {
    const stored = window.localStorage.getItem(DEV_ID_KEY);
    if (stored) return Number(stored);
    const generated = 100_000 + Math.floor(Math.random() * 800_000);
    window.localStorage.setItem(DEV_ID_KEY, String(generated));
    return generated;
  } catch {
    return 100_001;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent as `Idempotency-Key`; required by every money-moving endpoint. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Internal: prevents an infinite re-auth loop. */
  retryOnExpiry?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal, retryOnExpiry = true } = options;

  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (session) headers.authorization = `Bearer ${session.token}`;
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;
  // The phone: one account per device (services/device.ts).
  Object.assign(headers, deviceHeaders());

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new NetworkError(error);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? safeParse(text) : null;

  if (!response.ok) {
    const envelope = payload as ApiErrorBody | null;
    const code = envelope?.error?.code ?? 'INTERNAL_ERROR';
    const message = envelope?.error?.message ?? 'Something went wrong.';

    // The session is short-lived by design; Telegram hands us a fresh initData
    // on every launch, so we can silently sign back in and retry once.
    if (code === 'TOKEN_EXPIRED' && retryOnExpiry) {
      clearSession();
      try {
        await authenticate();
      } catch (authError) {
        reportSuspended(authError);
        throw authError;
      }
      return request<T>(path, { ...options, retryOnExpiry: false });
    }

    const failure = new ApiError(code, message, response.status, envelope?.error?.details ?? null);
    reportSuspended(failure);
    throw failure;
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
